// app/src/lib/nav.tsx — the live navigation tree.
//
// One fetch of /api/navigation (defaults + the admin's overrides, filtered by
// the caller's permissions), shared by the section tabs, the dashboard grid and
// the mobile sheet so the three can never disagree.
//
// DEFAULT_NAV is the initial value, not a loading state: the tabs render
// immediately from the built-in tree and swap to the customised one when the
// response lands. A failed request leaves the defaults in place — a shop with a
// flaky connection still gets a usable back office.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import { DEFAULT_NAV, type NavSection } from "../../../shared/nav";

const Ctx = createContext<{ sections: NavSection[]; reload: () => void }>({ sections: DEFAULT_NAV, reload: () => {} });

export function NavProvider({ children }: { children: ReactNode }) {
  const [sections, setSections] = useState<NavSection[]>(DEFAULT_NAV);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    api.get<{ sections: NavSection[] }>("/navigation")
      .then((r) => { if (live && r.sections?.length) setSections(r.sections); })
      .catch(() => { /* keep the built-in tree */ });
    return () => { live = false; };
  }, [tick]);
  const value = useMemo(() => ({ sections, reload: () => setTick((t) => t + 1) }), [sections]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNav(): NavSection[] { return useContext(Ctx).sections; }
export function useNavReload(): () => void { return useContext(Ctx).reload; }

/** The nav item that owns a path: exact match, else the longest `to` that
 *  prefixes it, so /workshop/diary resolves to Diary and /workshop/abc to Jobs. */
export function navItemFor(sections: NavSection[], path: string) {
  let best: { cat: NavSection; item: NavSection["items"][number] } | null = null;
  for (const cat of sections) for (const item of cat.items) {
    if (path === item.to || path.startsWith(item.to + "/")) {
      if (!best || item.to.length > best.item.to.length) best = { cat, item };
    }
  }
  return best;
}
